import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import api from "../../store/services/api";

// SERVICES
import MerchantsService from "../../api/merchant";

// APP COMPONENTS
import NameFilter from "../search/nameFilter";
import RemoveModal from "../modals/removeModal";
import SearchFilter from "../search/searchFilter";
import ConfirmModal from "../modals/confirmModal";
import ImportedItems from "./importCatalog/importedItems";
import ProductModal from "./products/ProductModal";
import CategoryModal from "./categories/CategoryModal";
import ComplementModal from "./complements/ComplementModal";
import ImportExcelFileModal from "./ImportItemsFromExcel/importExcelFileModal";
import ImportStatusAndInfos from "./importCatalog/importStatusAndInfos";
import ImportProgressModal from "./importCatalog/ImportProgressModal";
import ItemFiscalInfosModal from "./products/itemFiscalInfosModal";
import HasFiscalFilter from "../search/HasFiscalFilter";

// API
import { getProducts } from "../../api/product";
import { getComplements } from "../../api/complement";
import { dispatchSnackBarMessage } from "../../utils/snackbar";

// MATERIAL UI COMPONENTS
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Container from "@mui/material/Container";
import ToggleButton from "@mui/material/ToggleButton";
import Skeleton from "@mui/material/Skeleton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { alpha, useTheme } from "@mui/material/styles";

// STYLED COMPONENTS
import {
  StyledAccordion,
  StyledAccordionDetails,
  StyledAccordionSummary,
} from "../styled";

// ICONS
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import UploadIcon from "@mui/icons-material/Upload";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import FastfoodIcon from "@mui/icons-material/Fastfood";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import ExtensionIcon from "@mui/icons-material/Extension";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import EmptyStateIcon from "@mui/icons-material/SentimentDissatisfied";
import MenuProductRow, { MenuProductList } from "./table/menuProductRow";
import CategoryRow, { CategoryList } from "./table/categoryRow";
import PageHeaderLight from "../ui/PageHeaderLight";
import FilterListIcon from "@mui/icons-material/FilterList";

// Animation variants
const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.4 } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const fromTop = {
  hidden: { y: -20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 20,
    },
  },
};

const buttonVariants = {
  hover: { scale: 1.05 },
  tap: { scale: 0.95 },
};

export default function ProductsIndex() {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [products, setProducts] = useState([]);
  const [complements, setComplements] = useState([]);
  const [categories, setCategories] = useState([]);
  const [ifoodMerchants, setIfoodMerchants] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingComplement, setEditingComplement] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [restaurantToImportCatalog, setRestaurantToImportCatalog] =
    useState(null);
  const [filters, setFilters] = useState({
    newSearch: false,
    title: "",
    hasFiscal: "",
  });
  const [showButtonMore, setShowButtonMore] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeleteComplementModal, setShowDeleteComplementModal] =
    useState(false);
  const [showDeleteCategoryModal, setShowDeleteCategoryModal] = useState(false);
  const [showNewProductModal, setShowNewProductModal] = useState(false);
  const [showNewComplementModal, setShowNewComplementModal] = useState(false);
  const [showNewCategoryModal, setShowNewCategoryModal] = useState(false);
  const [importExcelFileType, setImportExcelFileType] = useState(false);
  const [showImportExcelModal, setShowImportExcelModal] = useState(false);
  const [showWhat, setShowWhat] = useState("categories");
  const [showProductsList, setShowProductsList] = useState(false);
  const [showComplementsList, setShowComplementsList] = useState(false);
  const [showCategoriesList, setShowCategoriesList] = useState(true);
  const [showIfoodMenuList, setShowIfoodMenuList] = useState(false);
  const [showIfoodMenuModal, setShowIfoodMenuModal] = useState(false);
  const [showImportProgress, setShowImportProgress] = useState(false);
  const [importRefreshKey, setImportRefreshKey] = useState(0);
  const [showItemFiscalInfosModal, setShowItemFiscalInfosModal] =
    useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [complementToDelete, setComplementToDelete] = useState(null);
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  const [expanded, setExpanded] = useState(null);

  const [openErrorAlert, setOpenErrorAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [expandedFilters, setExpandedFilters] = useState(false);

  useEffect(() => {
    handleShowWhat(null, "categories").then((r) => r);
  }, []);

  const search = async (filtersData) => {
    setPage(1);
    try {
      const filtersObj = {
        page: page,
        title: filtersData[NameFilter.name] ?? "",
        hasFiscal: filtersData[HasFiscalFilter.name] ?? "",
      };
      setFilters(filtersObj);

      if (showProductsList) {
        await loadProducts(true, filtersObj);
      } else if (showComplementsList) {
        await loadComplements(true, filtersObj);
      } else if (showCategoriesList) {
        await loadCategories(true, filtersObj);
      } else {
        await loadIfoodMerchants();
      }
    } catch (e) {}
  };

  const loadProducts = async (newSearch, filtersData = null) => {
    try {
      setLoading(true);

      let pageQuery = page;
      if (newSearch) {
        pageQuery = 1;
        setPage(1);
      }

      const params = {
        page: pageQuery,
        title: filtersData?.title || filters.title,
        hasFiscal: filtersData?.hasFiscal || filters.hasFiscal,
      };

      const { data } = await getProducts(params);

      let result;
      if (newSearch) {
        result = [...data];
        switchShowButtonMore(data.length);
      } else {
        const new_products = data.filter(
          ({ product_id: id1 }) =>
            !products.some(({ product_id: id2 }) => id2 === id1)
        );
        result = [...products, ...new_products];
        switchShowButtonMore(new_products.length);
      }

      setPage(pageQuery + 1);
      setProducts(result);
      setLoading(false);
    } catch (e) {}
  };

  const loadComplements = async (newSearch, filtersData = null) => {
    try {
      setLoading(true);

      let pageQuery = page;
      if (newSearch) {
        pageQuery = 1;
        setPage(1);
      }

      const params = {
        page: pageQuery,
        title: filtersData?.title || filters.title,
        hasFiscal: filtersData?.hasFiscal || filters.hasFiscal,
      };

      const { data } = await getComplements(params);

      let result;
      if (newSearch) {
        result = [...data];
        switchShowButtonMore(data.length);
      } else {
        const new_complements = data.filter(
          ({ complement_id: id1 }) =>
            !products.some(({ complement_id: id2 }) => id2 === id1)
        );
        result = [...products, ...new_complements];
        switchShowButtonMore(new_complements.length);
      }

      setPage(pageQuery + 1);
      setComplements(result);
      setLoading(false);
    } catch (e) {}
  };

  const loadCategories = async (newSearch, filtersData = null) => {
    try {
      setLoading(true);

      let pageQuery = page;
      if (newSearch) {
        pageQuery = 1;
        setPage(1);
      }

      const filtersQuery = `?page=${pageQuery}&title=${
        filtersData ? filtersData.title : filters.title
      }`;
      const { data } = await api.get("/products/categories/" + filtersQuery);

      let result;
      if (newSearch) {
        result = [...data];
        switchShowButtonMore(data.length);
      } else {
        const new_categories = data.filter(
          ({ complement_id: id1 }) =>
            !products.some(({ complement_id: id2 }) => id2 === id1)
        );
        result = [...products, ...new_categories];
        switchShowButtonMore(new_categories.length);
      }

      setPage(pageQuery + 1);
      setCategories(result);
      setLoading(false);
    } catch (e) {}
  };

  const loadIfoodMerchants = async () => {
    try {
      setLoading(true);
      const res = await MerchantsService.getMerchants({
        ifood: true,
      });
      setIfoodMerchants(res.data);
      setLoading(false);
    } catch (e) {
      // Don't fail silently — a failed merchant load leaves the import screen
      // empty with no explanation.
      console.error("Failed to load iFood merchants", e?.response?.status, e?.response?.data || e?.message);
      setLoading(false);
    }
  };

  const handleShowWhat = async (event, value) => {
    setShowWhat(value);
    setPage(1);
    switch (value) {
      case "products":
        setShowProductsList(true);
        setShowComplementsList(false);
        setShowCategoriesList(false);
        setShowIfoodMenuList(false);
        await loadProducts(true);
        break;

      case "complements":
        setShowComplementsList(true);
        setShowProductsList(false);
        setShowCategoriesList(false);
        setShowIfoodMenuList(false);
        await loadComplements(true);
        break;

      case "categories":
        setShowCategoriesList(true);
        setShowComplementsList(false);
        setShowProductsList(false);
        setShowIfoodMenuList(false);
        await loadCategories(true);
        break;

      case "ifood-menu":
        setShowIfoodMenuList(true);
        setShowCategoriesList(false);
        setShowComplementsList(false);
        setShowProductsList(false);
        await loadIfoodMerchants();
        break;

      default:
        break;
    }
  };

  const switchShowButtonMore = (length) => {
    length > 0 ? setShowButtonMore(true) : setShowButtonMore(false);
  };

  const handleDeleteProductModal = (product) => {
    setShowDeleteModal(!showDeleteModal);
    if (
      product &&
      !(product instanceof Event || product.nativeEvent instanceof Event)
    ) {
      setProductToDelete(product);
    }
  };

  const handleDeleteProduct = async () => {
    try {
      await api.delete(`/products/?pid=${productToDelete.product_id}`);
      setShowDeleteModal(false);
      setProductToDelete(null);
      await loadProducts(true);
    } catch (e) {
      dispatchSnackBarMessage(
        `product-delete-${Date.now()}`,
        "Erro ao deletar produto",
        "error"
      );
    }
  };

  const handleDeleteComplementModal = (complement) => {
    setShowDeleteComplementModal(!showDeleteComplementModal);
    if (
      complement &&
      !(complement instanceof Event || complement.nativeEvent instanceof Event)
    ) {
      setComplementToDelete(complement);
    }
  };

  const handleDeleteComplement = async () => {
    try {
      await api.delete(`/complements/?cid=${complementToDelete.complement_id}`);
      setComplementToDelete(null);
      await loadComplements(true);
    } catch (e) {
      dispatchSnackBarMessage(
        `complement-delete-${Date.now()}`,
        "Erro ao deletar complemento",
        "error"
      );
    }
  };

  const handleDeleteCategoryModal = (category) => {
    setShowDeleteCategoryModal(!showDeleteCategoryModal);
    if (
      category &&
      !(category instanceof Event || category.nativeEvent instanceof Event)
    ) {
      setCategoryToDelete(category);
    }
  };

  const handleDeleteCategory = async () => {
    try {
      await api.delete(`/products/categories/?id=${categoryToDelete.id}`);
      setCategoryToDelete(null);
      await loadCategories(true);
    } catch (e) {
      dispatchSnackBarMessage(
        `category-delete-${Date.now()}`,
        "Erro ao deletar categoria",
        "error"
      );
    }
  };

  const handleNewProductModal = (e, product = null) => {
    setEditingProduct(product);
    setShowNewProductModal(!showNewProductModal);
  };

  const handleNewComplementModal = (e, complement = null) => {
    setEditingComplement(complement);
    setShowNewComplementModal(!showNewComplementModal);
  };

  const handleNewCategoryModal = (e, category = null) => {
    setEditingCategory(category);
    setShowNewCategoryModal(!showNewCategoryModal);
  };

  const handleImportExcelModal = (importType) => {
    setImportExcelFileType(importType);
    setShowImportExcelModal(!showImportExcelModal);
  };

  const handleProductAction = async (isEditing, product) => {
    if (isEditing) {
      await editProduct(product);
    } else {
      await createProduct(product);
    }
  };

  const createProduct = async (product) => {
    try {
      const res = await api.post("/products/", product, {
        headers: { "Content-Type": "application/json" },
      });

      if (res.status === 201) {
        // Run complementary and category creation operations concurrently
        await Promise.all([
          handleComplementsToCreate(product.complements, product.product_id),
          handleCategoryProductsToCreateFromProduct(
            product.categories,
            product.product_id
          ),
        ]);

        // Reload products
        await loadProducts(true);
      }
    } catch (error) {
      console.error("Error creating product:", error);
      dispatchSnackBarMessage(
        `product-create-${Date.now()}`,
        "Erro ao criar produto",
        "error"
      );
    }
  };

  const editProduct = async (product) => {
    try {
      const {
        product_id,
        complements,
        complementsToDelete,
        categories,
        categoriesListToDelete,
      } = product;

      console.log("editProduct product_id", product_id);

      await api.put("/products/", product, {
        params: { pid: product_id },
        headers: { "Content-Type": "application/json" },
      });

      const promises = [];

      if (complements && complements.length > 0) {
        promises.push(handleComplementsToCreate(complements, product_id));
      }

      if (complementsToDelete && complementsToDelete.length > 0) {
        promises.push(
          handleComplementsToDelete(complementsToDelete, product_id)
        );
      }

      if (categories && categories.length > 0) {
        promises.push(
          handleCategoryProductsToCreateFromProduct(categories, product_id)
        );
      }

      if (categoriesListToDelete && categoriesListToDelete.length > 0) {
        promises.push(
          handleCategoryProductsToDeleteFromProduct(
            categoriesListToDelete,
            product_id
          )
        );
      }

      await Promise.all(promises);
      await loadProducts(true);
    } catch (error) {
      console.error("Error editing product:", error);
      dispatchSnackBarMessage(
        `product-edit-${Date.now()}`,
        "Erro ao atualizar produto",
        "error"
      );
    }
  };

  const handleComplementsToCreate = (toCreate, product_id) => {
    // Map each complement creation operation to a promise
    const complementPromises = toCreate.map((complement) => {
      return createProductComplement({
        product: product_id,
        complement: complement.complement_id,
      });
    });

    // Return a promise that resolves when all complement creation operations are complete
    return Promise.all(complementPromises);
  };

  const handleComplementsToDelete = (toDelete, product_id) => {
    // Map each complement deletion operation to a promise
    const deletionPromises = toDelete.map((complementToDelete) => {
      return deleteProductComplement(product_id, complementToDelete.complement);
    });

    // Return a promise that resolves when all complement deletion operations are complete
    return Promise.all(deletionPromises);
  };

  const handleComplementAction = async (isEditing, complement) => {
    if (isEditing) {
      await editComplement(complement);
    } else {
      await createComplement(complement);
    }
  };

  const createComplement = async (complement) => {
    try {
      const res = await api.post("/complements/", complement, {
        headers: { "Content-Type": "application/json" },
      });
      if (res.status === 201) {
        handleComplementsProductsToCreate(
          complement.productsComplementsToCreate,
          complement.complement_id
        );
        await loadComplements(true);
      }
    } catch (e) {
      console.error(e);
      dispatchSnackBarMessage(
        `complement-create-${Date.now()}`,
        "Erro ao criar complemento",
        "error"
      );
    }
  };

  const editComplement = async (complement) => {
    try {
      const { complement_id } = complement;
      await api.put("/complements/", complement, {
        params: { cid: complement.complement_id },
        headers: { "Content-Type": "application/json" },
      });
      handleComplementsProductsToCreate(
        complement.productsComplementsToCreate,
        complement_id
      );
      handleComplementsProductsToDelete(
        complement.complementsToDelete,
        complement_id
      );
      await loadComplements(true);
    } catch (e) {
      console.error(e);
      dispatchSnackBarMessage(
        `complement-edit-${Date.now()}`,
        "Erro ao atualizar complemento",
        "error"
      );
    }
  };

  const handleCategoryAction = async (isEditing, complement) => {
    if (isEditing) {
      await editCategory(complement);
    } else {
      await createCategory(complement);
    }
  };

  const createCategory = async (category) => {
    try {
      const res = await api.post("/products/categories/", category, {
        headers: { "Content-Type": "application/json" },
      });
      if (res.status === 201) {
        await handleCategoryProductsToCreate(
          category.categoryProductsToCreate,
          res.data.id
        );
        await loadCategories(true);
      }
    } catch (e) {
      console.error(e);
      dispatchSnackBarMessage(
        `category-create-${Date.now()}`,
        "Erro ao criar categoria",
        "error"
      );
    }
  };

  const editCategory = async (category) => {
    try {
      const { id } = category;
      await api.put("/products/categories/", category, {
        params: { id: category.id },
        headers: { "Content-Type": "application/json" },
      });
      await handleCategoryProductsToCreate(
        category.categoryProductsToCreate,
        id
      );
      await handleCategoryProductsToDelete(
        category.categoryProductsToDelete,
        id
      );
      await loadCategories(true);
    } catch (e) {
      console.error(e);
      dispatchSnackBarMessage(
        `category-edit-${Date.now()}`,
        "Erro ao atualizar categoria",
        "error"
      );
    }
  };

  const handleCategoryProductsToCreate = (toCreate, category_id) => {
    // Map each category product creation operation to a promise
    const creationPromises = toCreate.map((product) => {
      return createCategoryProduct({
        product: product.product_id,
        category: category_id,
      });
    });

    // Return a promise that resolves when all category product creation operations are complete
    return Promise.all(creationPromises);
  };

  const handleCategoryProductsToCreateFromProduct = (toCreate, product_id) => {
    // Map each category product creation operation to a promise
    const creationPromises = toCreate.map((category) => {
      return createCategoryProduct({
        product: product_id,
        category: category.id,
      });
    });

    // Return a promise that resolves when all category product creation operations are complete
    return Promise.all(creationPromises);
  };

  const handleCategoryProductsToDelete = (toDelete, category_id) => {
    // Map each category product deletion operation to a promise
    const deletionPromises = toDelete.map((product) => {
      return deleteCategoryProduct(product.product, category_id);
    });

    // Return a promise that resolves when all category product deletion operations are complete
    return Promise.all(deletionPromises);
  };

  const handleCategoryProductsToDeleteFromProduct = (toDelete, product_id) => {
    // Map each category product deletion operation to a promise
    const deletionPromises = toDelete.map((category) => {
      return deleteCategoryProduct(product_id, category.id);
    });

    // Return a promise that resolves when all category product deletion operations are complete
    return Promise.all(deletionPromises);
  };

  const createCategoryProduct = async (categoryProduct) => {
    try {
      await api.post("/products/categories/product/", categoryProduct, {
        headers: { "Content-Type": "application/json" },
      });
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  const deleteCategoryProduct = async (product_id, category_id) => {
    try {
      await api.delete("/products/categories/product/", {
        params: {
          product_id: product_id,
          category_id: category_id,
        },
        headers: { "Content-Type": "application/json" },
      });
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  const handleComplementsProductsToCreate = (toCreate, complement_id) => {
    toCreate.forEach((product) => {
      createProductComplement({
        product: product.product_id,
        complement: complement_id,
      }).then((r) => r);
    });
  };

  const handleComplementsProductsToDelete = (toDelete, complement_id) => {
    toDelete.forEach((complementToDelete) => {
      deleteProductComplement(complementToDelete.product, complement_id).then(
        (r) => r
      );
    });
  };

  const createProductComplement = async (productComplement) => {
    try {
      await api.post("/products/complements/", productComplement, {
        headers: { "Content-Type": "application/json" },
      });
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  const deleteProductComplement = async (product_id, complement_id) => {
    try {
      await api.delete("/products/complements/", {
        params: {
          product_id: product_id,
          complement_id: complement_id,
        },
        headers: { "Content-Type": "application/json" },
      });
    } catch (e) {
      console.log(e);
    }
  };

  const toggleProductAvailable = async (product) => {
    try {
      await api.put(
        `/products/availability/?id=${
          product.product_id
        }&isAvailable=${!product.available}`,
        {}
      );
      product.available = !product.available;
      setProducts([...products]);
    } catch (e) {
      console.error(e);
      dispatchSnackBarMessage(
        `product-available-${Date.now()}`,
        "Erro ao atualizar status do produto",
        "error"
      );
    }
  };

  const toggleComplementAvailable = async (complement) => {
    try {
      await api.put(
        `/complements/availability/?id=${
          complement.complement_id
        }&isAvailable=${!complement.available}`,
        {}
      );
      complement.available = !complement.available;
      setComplements([...complements]);
    } catch (e) {
      console.error(e);
      dispatchSnackBarMessage(
        `product-available-${Date.now()}`,
        "Erro ao atualizar status do complemento",
        "error"
      );
    }
  };

  const handleIfoodMenuImport = (restaurant = null, event) => {
    if (event) {
      event.stopPropagation();
    }
    setRestaurantToImportCatalog(restaurant);
    setShowIfoodMenuModal(!showIfoodMenuModal);
  };

  const handleItemFiscalInfosModal = (item) => {
    setEditingProduct(item);
    setShowItemFiscalInfosModal(!showItemFiscalInfosModal);
  };

  // Build a human-readable message from an axios error coming off the iFood
  // import POST. Handles: no response (network/timeout), DRF string error
  // ({error: "..."}), DRF field errors ({fk_merchant: ["..."]}), plain detail.
  const getImportErrorMessage = (e) => {
    if (!e?.response) {
      return "Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente.";
    }
    const data = e.response.data;
    if (data) {
      if (typeof data === "string") return data;
      if (data.error) return data.error;
      if (data.detail) return data.detail;
      const firstField = Object.values(data)[0];
      if (Array.isArray(firstField) && firstField.length) return firstField[0];
      if (typeof firstField === "string") return firstField;
    }
    return `Erro ao importar o cardápio (código ${e.response.status}). Tente novamente.`;
  };

  const handleImportIfoodCatalog = async () => {
    try {
      await MerchantsService.createMerchantsIfoodImport(
        restaurantToImportCatalog
      );
      setShowIfoodMenuModal(false);
      setShowImportProgress(true);
    } catch (e) {
      // Surface the real reason instead of crashing the handler. A DRF 400
      // returns field errors (e.g. {"fk_merchant": [...]}) with no `.error`
      // key, and a network/timeout error has no `e.response` at all — the old
      // `e.response.data.error` threw a TypeError here, so the import failed
      // silently (no modal, no message, no row created).
      console.error("iFood catalog import failed", e?.response?.status, e?.response?.data || e?.message);
      setErrorMessage(getImportErrorMessage(e));
      setOpenErrorAlert(true);
      setShowIfoodMenuModal(false);
    }
  };

  const handleCloseImportProgress = (finished) => {
    setShowImportProgress(false);
    if (finished) {
      // Refresh the status badge / imported-items list once the import ends
      setImportRefreshKey((k) => k + 1);
    }
  };

  const handleExpandedChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const handleEditImportedItem = async (item) => {
    if (item.is_product) {
      await editProduct(item);
    } else {
      await editComplement(item);
    }
  };

  const toggleExpandedFilters = () => {
    setExpandedFilters(!expandedFilters);
  };

  // Function to render the appropriate icon for the view
  const getViewIcon = () => {
    switch (showWhat) {
      case "products":
        return <FastfoodIcon sx={{ fontSize: 48, opacity: 0.8 }} />;
      case "complements":
        return <ExtensionIcon sx={{ fontSize: 48, opacity: 0.8 }} />;
      case "categories":
        return <LocalOfferIcon sx={{ fontSize: 48, opacity: 0.8 }} />;
      case "ifood-menu":
        return <RestaurantIcon sx={{ fontSize: 48, opacity: 0.8 }} />;
      default:
        return null;
    }
  };

  // Function to get the title for the current view
  const getViewTitle = () => {
    switch (showWhat) {
      case "products":
        return "Produtos";
      case "complements":
        return "Complementos";
      case "categories":
        return "Categorias";
      case "ifood-menu":
        return "Menu iFood";
      default:
        return "";
    }
  };

  // Function to render the appropriate button for the view
  const getAddButton = () => {
    if (showWhat === "products") {
      return (
        <>
          <motion.div
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
          >
            <Button
              variant="contained"
              color="primary"
              fullWidth
              startIcon={<AddIcon />}
              onClick={handleNewProductModal}
              sx={{
                mr: 1,
                mb: 1,
                borderRadius: 2,
                px: 2,
                py: 1,
              }}
            >
              Novo Produto
            </Button>
          </motion.div>

          <motion.div
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
          >
            <Button
              variant="outlined"
              fullWidth
              startIcon={<UploadIcon />}
              onClick={() => handleImportExcelModal("PRODUCT")}
              sx={{
                borderRadius: 2,
                px: 2,
                py: 1,
              }}
            >
              Importar Produtos
            </Button>
          </motion.div>
        </>
      );
    } else if (showWhat === "complements") {
      return (
        <>
          <motion.div
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
          >
            <Button
              variant="contained"
              color="primary"
              fullWidth
              startIcon={<AddIcon />}
              onClick={handleNewComplementModal}
              sx={{
                mr: 1,
                mb: 1,
                borderRadius: 2,
                px: 2,
                py: 1,
              }}
            >
              Novo Complemento
            </Button>
          </motion.div>

          <motion.div
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
          >
            <Button
              variant="outlined"
              fullWidth
              startIcon={<UploadIcon />}
              onClick={() => handleImportExcelModal("COMPLEMENT")}
              sx={{
                borderRadius: 2,
                px: 2,
                py: 1,
              }}
            >
              Importar Complementos
            </Button>
          </motion.div>
        </>
      );
    } else if (showWhat === "categories") {
      return (
        <motion.div variants={buttonVariants} whileHover="hover" whileTap="tap">
          <Button
            variant="contained"
            color="primary"
            fullWidth
            startIcon={<AddIcon />}
            onClick={handleNewCategoryModal}
            sx={{
              borderRadius: 2,
              px: 2,
              py: 1,
            }}
          >
            Nova Categoria
          </Button>
        </motion.div>
      );
    }
    return null;
  };

  // Function to render empty state
  const renderEmptyState = () => {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: 8,
          opacity: 0.7,
        }}
      >
        <EmptyStateIcon sx={{ fontSize: 64, mb: 2, opacity: 0.6 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Nenhum item encontrado
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center">
          {showProductsList &&
            "Nenhum produto encontrado. Adicione um novo produto ou refine sua busca."}
          {showComplementsList &&
            "Nenhum complemento encontrado. Adicione um novo complemento ou refine sua busca."}
          {showCategoriesList &&
            "Nenhuma categoria encontrada. Adicione uma nova categoria ou refine sua busca."}
          {showIfoodMenuList && "Nenhum restaurante iFood encontrado."}
        </Typography>
        {(showProductsList || showComplementsList || showCategoriesList) && (
          <Box sx={{ mt: 3 }}>{getAddButton()}</Box>
        )}
      </Box>
    );
  };

  const pageHeaderActions = [
    {
      key: "filtros",
      variant: "outlined",
      startIcon: <FilterListIcon />,
      onClick: toggleExpandedFilters,
      children: "Filtros",
    },
  ];

  return (
    <motion.div initial="hidden" animate="visible" variants={fadeIn}>
      <Container maxWidth="lg" sx={{ mt: 3, mb: 6 }}>
        <PageHeaderLight
          title="Menu"
          subtitle="Gerencie os seus menus"
          actions={pageHeaderActions}
          filters={
            <SearchFilter
              title=""
              filters={[NameFilter, HasFiscalFilter]}
              initialExpanded
              submit={search}
            />
          }
          expandedFilters={expandedFilters}
          toggleFilters={toggleExpandedFilters}
        />

        <Grid container spacing={3}>
          <Grid item xs={12} md={4} lg={3}>
            <motion.div variants={fromTop}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  height: "100%",
                  backgroundColor: alpha(theme.palette.background.paper, 0.8),
                  backdropFilter: "blur(10px)",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                }}
              >
                <Typography variant="h6" fontWeight="600" sx={{ mb: 2 }}>
                  Visualizar
                </Typography>

                <Divider sx={{ mb: 2 }} />

                <ToggleButtonGroup
                  value={showWhat}
                  exclusive
                  onChange={handleShowWhat}
                  orientation="vertical"
                  fullWidth
                  sx={{
                    "& .MuiToggleButton-root": {
                      p: 2,
                      mb: 1,
                      justifyContent: "flex-start",
                      borderRadius: 2,
                      border: "none",
                      "&.Mui-selected": {
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                        fontWeight: "bold",
                      },
                    },
                  }}
                >
                  <ToggleButton value="categories" aria-label="show categories">
                    <LocalOfferIcon sx={{ mr: 1 }} />
                    Categorias
                  </ToggleButton>
                  <ToggleButton value="products" aria-label="show products">
                    <FastfoodIcon sx={{ mr: 1 }} />
                    Produtos
                  </ToggleButton>
                  <ToggleButton
                    value="complements"
                    aria-label="show complements"
                  >
                    <ExtensionIcon sx={{ mr: 1 }} />
                    Complementos
                  </ToggleButton>
                  <ToggleButton
                    value="ifood-menu"
                    aria-label="show ifood-menu"
                    sx={{
                      color: theme.palette.error.main,
                      "&.Mui-selected": {
                        backgroundColor: alpha(theme.palette.error.main, 0.1),
                        color: theme.palette.error.main,
                      },
                    }}
                  >
                    <RestaurantIcon sx={{ mr: 1 }} />
                    Menu iFood
                  </ToggleButton>
                </ToggleButtonGroup>

                <Box sx={{ mt: 4 }}>{getAddButton()}</Box>
              </Paper>
            </motion.div>
          </Grid>

          <Grid item xs={12} md={8} lg={9}>
            <AnimatePresence>
              {openErrorAlert && (
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                >
                  <Alert
                    variant="filled"
                    severity="error"
                    action={
                      <IconButton
                        aria-label="close"
                        color="inherit"
                        size="small"
                        onClick={() => {
                          setOpenErrorAlert(false);
                        }}
                      >
                        <CloseIcon fontSize="inherit" />
                      </IconButton>
                    }
                    sx={{ mb: 3, borderRadius: 2 }}
                  >
                    {errorMessage}
                  </Alert>
                </motion.div>
              )}
            </AnimatePresence>

            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                overflow: "hidden",
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                backdropFilter: "blur(10px)",
                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
              }}
            >
              <Box
                sx={{
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  borderBottom: 1,
                  borderColor: "divider",
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                      borderRadius: 1.5,
                      p: 0.75,
                      width: 40,
                      height: 40,
                    }}
                  >
                    {getViewIcon()}
                  </Box>
                  <Typography variant="h6" fontWeight="600">
                    {getViewTitle()}
                  </Typography>
                </Stack>
              </Box>

              <Divider />

              <Box sx={{ p: 0 }}>
                {loading ? (
                  <Box sx={{ p: 3 }}>
                    {[1, 2, 3, 4].map((_, index) => (
                      <Skeleton
                        key={index}
                        variant="rectangular"
                        height={80}
                        sx={{
                          mb: 2,
                          borderRadius: 2,
                          opacity: 1 - index * 0.2,
                        }}
                      />
                    ))}
                  </Box>
                ) : (
                  <Box>
                    {showProductsList && products.length > 0 && (
                      <Box sx={{ p: 2 }}>
                        <MenuProductList>
                          {products.map((product, index) => (
                            <MenuProductRow
                              key={product.product_id}
                              index={index + 1}
                              product={product}
                              deleteAction={() =>
                                handleDeleteProductModal(product)
                              }
                              editAction={() =>
                                handleNewProductModal(null, product)
                              }
                              isAvailable={product.available}
                              toggleAvailable={() =>
                                toggleProductAvailable(product)
                              }
                            />
                          ))}
                        </MenuProductList>
                      </Box>
                    )}

                    {showComplementsList && complements.length > 0 && (
                      <Box sx={{ p: 2 }}>
                        <MenuProductList>
                          {complements.map((complement, index) => (
                            <MenuProductRow
                              key={complement.complement_id}
                              index={index + 1}
                              product={complement}
                              deleteAction={() =>
                                handleDeleteComplementModal(complement)
                              }
                              editAction={() =>
                                handleNewComplementModal(null, complement)
                              }
                              isAvailable={complement.available}
                              toggleAvailable={() =>
                                toggleComplementAvailable(complement)
                              }
                            />
                          ))}
                        </MenuProductList>
                      </Box>
                    )}

                    {showCategoriesList && categories.length > 0 && (
                      <Box sx={{ p: 2 }}>
                        <CategoryList>
                          {categories.map((category, index) => (
                            <CategoryRow
                              key={category.id}
                              index={index + 1}
                              category={category}
                              deleteAction={() =>
                                handleDeleteCategoryModal(category)
                              }
                              editAction={() =>
                                handleNewCategoryModal(null, category)
                              }
                            />
                          ))}
                        </CategoryList>
                      </Box>
                    )}

                    {showIfoodMenuList && ifoodMerchants.length > 0 && (
                      <Box sx={{ p: 2 }}>
                        {ifoodMerchants.map((merchant, index) => (
                          <StyledAccordion
                            key={merchant.ifood_id}
                            expanded={expanded === `${merchant.name}Panel`}
                            onChange={handleExpandedChange(
                              `${merchant.name}Panel`
                            )}
                            variant="outlined"
                            sx={{
                              mb: 2,
                              borderRadius: 2,
                              overflow: "hidden",
                            }}
                          >
                            <StyledAccordionSummary
                              id="ifood-merchant-infos-header"
                              aria-controls="ifood-merchant-infos-content"
                              expandIcon={<ExpandMoreIcon />}
                            >
                              <Stack
                                direction="row"
                                spacing={2}
                                alignItems="center"
                                sx={{ flex: 1 }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    bgcolor: alpha(
                                      theme.palette.error.main,
                                      0.1
                                    ),
                                    color: theme.palette.error.main,
                                    borderRadius: 1.5,
                                    p: 0.75,
                                    width: 36,
                                    height: 36,
                                  }}
                                >
                                  <RestaurantIcon fontSize="small" />
                                </Box>
                                <Typography fontWeight={600}>
                                  {merchant.name}
                                </Typography>
                              </Stack>

                              <Stack
                                direction="row"
                                spacing={2}
                                alignItems="center"
                              >
                                <ImportStatusAndInfos
                                  key={`${merchant.ifood_id}-${importRefreshKey}`}
                                  ifood_id={merchant.ifood_id}
                                />
                                <Tooltip title="Importar Cardápio">
                                  <IconButton
                                    onClick={(e) =>
                                      handleIfoodMenuImport(merchant, e)
                                    }
                                    sx={{
                                      color: theme.palette.primary.main,
                                      bgcolor: alpha(
                                        theme.palette.primary.main,
                                        0.1
                                      ),
                                      p: 0.75,
                                      "&:hover": {
                                        bgcolor: alpha(
                                          theme.palette.primary.main,
                                          0.2
                                        ),
                                      },
                                    }}
                                  >
                                    <CloudDownloadIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </StyledAccordionSummary>
                            <StyledAccordionDetails>
                              {expanded === `${merchant.name}Panel` && (
                                <ImportedItems
                                  ifood_id={merchant.ifood_id}
                                  editProduct={handleItemFiscalInfosModal}
                                />
                              )}
                            </StyledAccordionDetails>
                          </StyledAccordion>
                        ))}
                      </Box>
                    )}

                    {/* Empty states */}
                    {showProductsList &&
                      products.length === 0 &&
                      renderEmptyState()}
                    {showComplementsList &&
                      complements.length === 0 &&
                      renderEmptyState()}
                    {showCategoriesList &&
                      categories.length === 0 &&
                      renderEmptyState()}
                    {showIfoodMenuList &&
                      ifoodMerchants.length === 0 &&
                      renderEmptyState()}

                    {/* Load more button */}
                    {((showProductsList && products.length > 0) ||
                      (showComplementsList && complements.length > 0) ||
                      (showCategoriesList && categories.length > 0)) && (
                      <Box sx={{ p: 2, pt: 0 }}>
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.3 }}
                        >
                          <Button
                            component={motion.div}
                            variants={buttonVariants}
                            whileHover="hover"
                            whileTap="tap"
                            title="Carregar mais"
                            size="large"
                            fullWidth
                            disabled={!showButtonMore}
                            startIcon={<ExpandMoreIcon />}
                            onClick={() => {
                              if (showProductsList) loadProducts(false);
                              else if (showComplementsList)
                                loadComplements(false);
                              else if (showCategoriesList)
                                loadCategories(false);
                            }}
                            sx={{
                              py: 1.5,
                              borderRadius: 2,
                              color: theme.palette.text.secondary,
                              bgcolor: alpha(
                                theme.palette.background.default,
                                0.5
                              ),
                              border: `1px solid ${alpha(
                                theme.palette.divider,
                                0.5
                              )}`,
                              "&:hover": {
                                bgcolor: alpha(
                                  theme.palette.background.default,
                                  0.8
                                ),
                              },
                            }}
                          >
                            {showButtonMore
                              ? "Mostrar Mais"
                              : "Não há mais itens para exibir"}
                          </Button>
                        </motion.div>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Modals */}
      <RemoveModal
        title="Deletar Produto"
        message={`Deseja deletar o produto ${productToDelete?.title}?`}
        show={showDeleteModal}
        toggleModal={handleDeleteProductModal}
        onRemove={handleDeleteProduct}
      />

      <RemoveModal
        title="Deletar Complemento"
        message={`Deseja deletar o complemento ${complementToDelete?.title}?`}
        show={showDeleteComplementModal}
        toggleModal={handleDeleteComplementModal}
        onRemove={handleDeleteComplement}
      />

      <RemoveModal
        title="Deletar Categoria"
        message={`Deseja deletar a categoria ${categoryToDelete?.title}?`}
        show={showDeleteCategoryModal}
        toggleModal={handleDeleteCategoryModal}
        onRemove={handleDeleteCategory}
      />

      {showNewProductModal && (
        <ProductModal
          product={editingProduct}
          closeModal={handleNewProductModal}
          show={showNewProductModal}
          createProduct={handleProductAction}
        />
      )}

      {showNewComplementModal && (
        <ComplementModal
          complement={editingComplement}
          closeModal={handleNewComplementModal}
          show={showNewComplementModal}
          createComplement={handleComplementAction}
        />
      )}

      {showNewCategoryModal && (
        <CategoryModal
          category={editingCategory}
          closeModal={handleNewCategoryModal}
          show={showNewCategoryModal}
          createCategory={handleCategoryAction}
        />
      )}

      {showImportExcelModal && (
        <ImportExcelFileModal
          show={showImportExcelModal}
          closeModal={handleImportExcelModal}
          importType={importExcelFileType}
          loadProducts={loadProducts}
          loadComplements={loadComplements}
        />
      )}

      {showItemFiscalInfosModal && (
        <ItemFiscalInfosModal
          show={showItemFiscalInfosModal}
          closeModal={handleItemFiscalInfosModal}
          product={editingProduct}
          updateProduct={handleEditImportedItem}
        />
      )}

      {showIfoodMenuModal && (
        <ConfirmModal
          show={showIfoodMenuModal}
          label={"Deseja Importar Cardápio Ifood?"}
          message={`Deseja Importar o Cardápio iFood de ${restaurantToImportCatalog?.name}?`}
          closeModal={handleIfoodMenuImport}
          action={handleImportIfoodCatalog}
        />
      )}

      {showImportProgress && (
        <ImportProgressModal
          show={showImportProgress}
          restaurant={restaurantToImportCatalog}
          onClose={handleCloseImportProgress}
        />
      )}
    </motion.div>
  );
}
